import { Component, ElementRef, HostListener, OnInit, OnDestroy } from '@angular/core';
import { formatDate } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DiscountService } from 'src/app/services/discount.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { BusinessService } from 'src/app/services/business.service';
import { CategoriasService } from 'src/app/services/categorias.service';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { DiscountChannel, DiscountOptions } from 'src/app/interfaces/discount';
import { AppSettings } from 'src/app/app-settings';
import { PanelTitleService } from 'src/app/services/panel-title.service';

@Component({
  selector: 'app-discount-form',
  templateUrl: './discount-form.component.html',
  styleUrls: ['./discount-form.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class DiscountFormComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isEdit: boolean = false;
  discountId: number | null = null;
  loaded: boolean = false;
  saving: boolean = false;

  options: DiscountOptions = {
    rule_types: [],
    discount_modes: [],
    application_targets: [],
    scope_types: [],
    channels: []
  };

  availableChannels: DiscountChannel[] = [];
  allChannelsEnabled: boolean = true;

  business: any;
  profile: any;
  isSuperAdmin: boolean = false;
  companyEvents: any[] = [];
  private eventsLoading: boolean = false;
  companySessions: any[] = [];
  eventSearchTerm: string = '';
  eventCategoryFilter: number | null = null;
  categoryDropdownOpen: boolean = false;
  eventCategories: any[] = [];
  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';

  // Track which event session-dropdowns are open
  openEventSessionDropdowns: Set<number> = new Set<number>();

  // Popover "añadir sesión" abierto para qué evento
  openAddSessionFor: number | null = null;

  hasBusinessChannel(...names: string[]): boolean {
    const active: any[] = this.business?.tpv_info?.sales_channel || [];
    if (active.length === 0) return false;
    return names.some(n => active.some((sc: any) => sc.name?.toLowerCase() === n.toLowerCase()));
  }

  private sub: Subscription;
  private businessSub: Subscription;

  // Datetime selector state
  startsAtDate: Date | null = null;
  startsAtTime: string | null = null;
  endsAtDate: Date | null = null;
  endsAtTime: string | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private discountService: DiscountService,
    private auth: AuthenticationService,
    private apiBusiness: ApiBusinessService,
    private businessService: BusinessService,
    private categoriasService: CategoriasService,
    private dialog: MatDialog,
    private elRef: ElementRef,
    private panelTitleService: PanelTitleService
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.categoryDropdownOpen) {
      const dropdown = this.elRef.nativeElement.querySelector('.category-dropdown');
      if (dropdown && !dropdown.contains(event.target)) {
        this.categoryDropdownOpen = false;
      }
    }
  }

  ngOnInit(): void {
    this.profile = this.auth.getProfile();
    const roles = this.profile?.roles?.map((r: any) => r.name) || [];
    this.isSuperAdmin = roles.includes('superadmin');

    this.initForm();
    this.loadOptions();
    this.loadCategories();

    this.business = JSON.parse(localStorage.getItem('business') || '{}');

    this.sub = this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEdit = true;
        this.discountId = +params['id'];
        this.loadDiscount(this.discountId);
      } else {
        this.loaded = true;
      }
    });

    this.businessSub = this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value && this.business?.id) {
          this.loadCompanyEvents();
        }
      }
    });
  }

  initForm() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      public_name: [''],
      rule_type: ['promo_code', Validators.required],
      discount_mode: ['percentage', Validators.required],
      discount_value: [0, [Validators.required, Validators.min(0)]],
      active: [true],
      starts_at: [null],
      ends_at: [null],
      priority: [0],
      min_quantity: [null],
      min_cart_amount: [null],
      buy_quantity: [null],
      pay_quantity: [null],
      application_target: [null],
      apply_scope: ['company', Validators.required],
      session_strict_mode: [false],
      event_ids: [[]],
      session_ids: [[]],
      // Promo code (single code for simplified flow)
      promo_code_value: [''],
      max_uses_global: [null],
      max_uses_per_customer: [null],
      // Promo codes (legacy FormArray)
      promo_codes: this.fb.array([]),
      // New fields
      max_discount_amount: [null],
      allowed_channels: [[]],
      // Bulk creation
      all_companies: [false],
    });
  }

  // Filter out 'pass' rule type - passes are managed in Abonos section
  get filteredRuleTypes(): string[] {
    return (this.options.rule_types || []).filter(rt => rt !== 'pass');
  }

  setRuleType(type: string) {
    this.form.patchValue({ rule_type: type });
    this.onRuleTypeChange();
  }

  generateCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.form.patchValue({ promo_code_value: code });
  }

  get promoCodes(): FormArray {
    return this.form.get('promo_codes') as FormArray;
  }

  addPromoCode() {
    this.promoCodes.push(this.fb.group({
      code: ['', Validators.required],
      max_uses: [null],
      uses_per_customer: [1, Validators.min(1)]
    }));
  }

  removePromoCode(index: number) {
    this.promoCodes.removeAt(index);
  }

  loadOptions() {
    this.discountService.getDiscountOptions().subscribe({
      next: (res) => {
        this.options = res;
        this.availableChannels = res.channels || [];
      }
    });
  }

  isChannelSelected(channelId: number): boolean {
    const selected: number[] = this.form.get('allowed_channels')?.value || [];
    return selected.includes(channelId);
  }

  toggleChannel(channelId: number) {
    const ctrl = this.form.get('allowed_channels')!;
    const current: number[] = [...(ctrl.value || [])];
    const idx = current.indexOf(channelId);
    if (idx === -1) {
      current.push(channelId);
    } else {
      current.splice(idx, 1);
    }
    ctrl.setValue(current);
  }

  toggleTaquilla() {
    const ctrl = this.form.get('allowed_channels')!;
    const current: number[] = [...(ctrl.value || [])];
    const hasBoth = current.includes(2) && current.includes(3);
    if (hasBoth) {
      ctrl.setValue(current.filter(id => id !== 2 && id !== 3));
    } else {
      const next = current.filter(id => id !== 2 && id !== 3);
      next.push(2, 3);
      ctrl.setValue(next);
    }
  }

  toggleAllChannels(enabled: boolean) {
    this.allChannelsEnabled = enabled;
    if (enabled) {
      this.form.get('allowed_channels')!.setValue([]);
    }
  }

  loadDiscount(id: number) {
    this.discountService.getDiscountById(id).subscribe({
      next: (res) => {
        const d = res.discount;
        this.panelTitleService.setSubtitle(d.name);
        this.form.patchValue({
          name: d.name,
          public_name: d.public_name || '',
          rule_type: d.rule_type,
          discount_mode: d.discount_mode,
          discount_value: d.discount_value,
          active: d.active,
          starts_at: d.starts_at ? d.starts_at.substring(0, 16) : null,
          ends_at: d.ends_at ? d.ends_at.substring(0, 16) : null,
          priority: d.priority,
          min_quantity: d.min_quantity,
          min_cart_amount: d.min_cart_amount,
          buy_quantity: d.buy_quantity,
          pay_quantity: d.pay_quantity,
          application_target: d.application_target,
          apply_scope: d.apply_scope,
          session_strict_mode: d.session_strict_mode ?? false,
          event_ids: d.events?.map((e: any) => e.id) || [],
          session_ids: d.sessions?.map((s: any) => s.id) || [],
          max_discount_amount: d.max_discount_amount ?? null,
          allowed_channels: d.allowed_channels ?? [],
        });

        this.onRuleTypeChange();

        // Set channel toggle state
        this.allChannelsEnabled = !d.allowed_channels || d.allowed_channels.length === 0;

        // Populate datetime-selector dates
        if (d.starts_at) {
          this.startsAtDate = new Date(d.starts_at);
          this.startsAtTime = formatDate(this.startsAtDate, 'HH:mm', 'es');
        }
        if (d.ends_at) {
          this.endsAtDate = new Date(d.ends_at);
          this.endsAtTime = formatDate(this.endsAtDate, 'HH:mm', 'es');
        }

        // Load promo code (simplified: use the first one)
        if (d.promo_codes?.length) {
          const pc = d.promo_codes[0];
          this.form.patchValue({
            promo_code_value: pc.code,
            max_uses_global: pc.max_uses,
            max_uses_per_customer: pc.uses_per_customer,
          });
        }

        this.loaded = true;
      },
      error: () => {
        this.openInfoCard('Error al cargar el descuento');
        this.router.navigate(['/panel-control/discounts']);
      }
    });
  }

  loadCompanyEvents() {
    if (!this.business?.id || this.eventsLoading) return;
    this.eventsLoading = true;

    const today = new Date().toISOString().split('T')[0];
    this.businessService.getEventsCompanyFilter(this.business.id, 1, '', null, [], false, true, null, true, true, null, undefined, today).subscribe({
      next: (res) => {
        this.companyEvents = (res.data?.data || []).filter((e: any) => !e.private_event);
        this.eventsLoading = false;
      },
      error: () => {
        this.eventsLoading = false;
      }
    });
  }

  loadCategories() {
    this.categoriasService.getAllCategories().subscribe({
      next: (res) => {
        this.eventCategories = res?.data?.data || [];
      }
    });
  }

  getSessionsForEvent(eventId: number): any[] {
    const event = this.companyEvents.find(e => e.id === eventId);
    return event?.sessions || [];
  }

  getEventDateLabel(event: any): string {
    const fmt = (d: Date) => d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const sessions: any[] = event?.sessions || [];
    if (sessions.length <= 1) {
      const raw = event?.start_date;
      return raw ? fmt(new Date(raw)) : '';
    }
    const sorted = [...sessions].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const first = new Date(sorted[0].start_date);
    const last = new Date(sorted[sorted.length - 1].start_date);
    const fmtShort = (d: Date) => d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' });
    return `${fmtShort(first)} – ${fmtShort(last)} · ${sessions.length} sesiones`;
  }

  getEventDisplayTime(event: any): string | null {
    if (event?.start_time) return event.start_time;
    const first = event?.sessions?.[0];
    if (first?.start_time) return first.start_time;
    // Si start_date viene como ISO con hora (ej: "2026-07-12T20:00:00") extraerla
    const raw: string = first?.start_date || event?.start_date;
    if (typeof raw === 'string' && raw.includes('T')) {
      const m = raw.match(/T(\d{2}:\d{2})/);
      if (m && m[1] !== '00:00') return m[1];
    }
    return null;
  }

  getSessionDisplayTime(session: any): string | null {
    if (session?.start_time) return session.start_time;
    const raw: string = session?.start_date;
    if (typeof raw === 'string' && raw.includes('T')) {
      const m = raw.match(/T(\d{2}:\d{2})/);
      if (m && m[1] !== '00:00') return m[1];
    }
    return null;
  }

  getSelectedEvents(): any[] {
    const ids: number[] = this.form.get('event_ids')?.value || [];
    return this.companyEvents.filter(e => ids.includes(e.id));
  }

  getFilteredEvents(): any[] {
    let events = this.companyEvents;
    if (this.eventCategoryFilter) {
      events = events.filter(e => (e.category?.id || e.id_event_category) === this.eventCategoryFilter);
    }
    if (this.eventSearchTerm?.trim()) {
      const term = this.eventSearchTerm.toLowerCase();
      events = events.filter(e => e.title?.toLowerCase().includes(term));
    }
    return events;
  }

  isEventSelected(eventId: number): boolean {
    const ids: number[] = this.form.get('event_ids')?.value || [];
    return ids.includes(eventId);
  }

  toggleEvent(eventId: number) {
    const ctrl = this.form.get('event_ids')!;
    const current: number[] = [...(ctrl.value || [])];
    const idx = current.indexOf(eventId);
    if (idx === -1) {
      current.push(eventId);
      // Auto-seleccionar todas las sesiones del evento si session_strict_mode está activo
      if (this.form.get('session_strict_mode')?.value) {
        this.addAllSessionsOfEvent(eventId);
      }
    } else {
      current.splice(idx, 1);
      // Quitar las sesiones asociadas al evento al deseleccionarlo
      this.removeAllSessionsOfEvent(eventId);
    }
    ctrl.setValue(current);
  }

  removeEvent(eventId: number) {
    const ctrl = this.form.get('event_ids')!;
    ctrl.setValue((ctrl.value || []).filter((id: number) => id !== eventId));
    this.removeAllSessionsOfEvent(eventId);
    this.openEventSessionDropdowns.delete(eventId);
    if (this.openAddSessionFor === eventId) this.openAddSessionFor = null;
  }

  // ===== Session selection helpers =====

  isSessionSelected(sessionId: number): boolean {
    const ids: number[] = this.form.get('session_ids')?.value || [];
    return ids.includes(sessionId);
  }

  toggleSession(sessionId: number, eventId: number) {
    const ctrl = this.form.get('session_ids')!;
    const current: number[] = [...(ctrl.value || [])];
    const idx = current.indexOf(sessionId);
    if (idx === -1) {
      current.push(sessionId);
    } else {
      // Evitar dejar el evento sin ninguna sesión seleccionada
      const remainingForEvent = this.getSessionsForEvent(eventId)
        .filter(s => s.id !== sessionId && current.includes(s.id));
      if (remainingForEvent.length === 0) {
        return; // No permitir quitar la última sesión
      }
      current.splice(idx, 1);
    }
    ctrl.setValue(current);
  }

  addAllSessionsOfEvent(eventId: number) {
    const sessions = this.getSessionsForEvent(eventId);
    if (!sessions.length) return;
    const ctrl = this.form.get('session_ids')!;
    const current: number[] = [...(ctrl.value || [])];
    sessions.forEach(s => {
      if (!current.includes(s.id)) current.push(s.id);
    });
    ctrl.setValue(current);
  }

  removeAllSessionsOfEvent(eventId: number) {
    const sessionIds = this.getSessionsForEvent(eventId).map(s => s.id);
    if (!sessionIds.length) return;
    const ctrl = this.form.get('session_ids')!;
    const current: number[] = (ctrl.value || []).filter((id: number) => !sessionIds.includes(id));
    ctrl.setValue(current);
  }

  getSelectedSessionsCount(eventId: number): number {
    const sessionIds = this.getSessionsForEvent(eventId).map(s => s.id);
    const selected: number[] = this.form.get('session_ids')?.value || [];
    return sessionIds.filter(id => selected.includes(id)).length;
  }

  getSelectedSessionsForEvent(eventId: number): any[] {
    const selected: number[] = this.form.get('session_ids')?.value || [];
    return this.getSessionsForEvent(eventId).filter(s => selected.includes(s.id));
  }

  // Devuelve la lista plana de pares evento+sesión seleccionados (para chips en strict mode)
  getSelectedEventSessionPairs(): { event: any; session: any }[] {
    const pairs: { event: any; session: any }[] = [];
    this.getSelectedEvents().forEach(event => {
      const sessions = this.getSelectedSessionsForEvent(event.id);
      sessions.forEach(session => pairs.push({ event, session }));
    });
    return pairs;
  }

  // Quita una sesión desde su chip. Si era la última del evento, quita también el evento.
  removeSessionChip(sessionId: number, eventId: number) {
    const ctrl = this.form.get('session_ids')!;
    const current: number[] = (ctrl.value || []).filter((id: number) => id !== sessionId);
    ctrl.setValue(current);
    const remaining = this.getSessionsForEvent(eventId).filter(s => current.includes(s.id));
    if (remaining.length === 0) {
      const evCtrl = this.form.get('event_ids')!;
      evCtrl.setValue((evCtrl.value || []).filter((id: number) => id !== eventId));
      this.openEventSessionDropdowns.delete(eventId);
      if (this.openAddSessionFor === eventId) this.openAddSessionFor = null;
    }
  }

  getUnselectedSessionsForEvent(eventId: number): any[] {
    const selected: number[] = this.form.get('session_ids')?.value || [];
    return this.getSessionsForEvent(eventId).filter(s => !selected.includes(s.id));
  }

  toggleAddSessionPopover(eventId: number) {
    this.openAddSessionFor = this.openAddSessionFor === eventId ? null : eventId;
  }

  addSessionToEvent(sessionId: number) {
    const ctrl = this.form.get('session_ids')!;
    const current: number[] = [...(ctrl.value || [])];
    if (!current.includes(sessionId)) {
      current.push(sessionId);
      ctrl.setValue(current);
    }
  }

  toggleEventSessionsDropdown(eventId: number) {
    if (this.openEventSessionDropdowns.has(eventId)) {
      this.openEventSessionDropdowns.delete(eventId);
    } else {
      this.openEventSessionDropdowns.add(eventId);
    }
  }

  isEventSessionsDropdownOpen(eventId: number): boolean {
    return this.openEventSessionDropdowns.has(eventId);
  }

  onSessionStrictModeChange() {
    const enabled = this.form.get('session_strict_mode')?.value;
    const selectedEventIds: number[] = this.form.get('event_ids')?.value || [];

    if (enabled) {
      // Al activar: auto-seleccionar todas las sesiones de los eventos ya elegidos
      selectedEventIds.forEach(eventId => this.addAllSessionsOfEvent(eventId));
    } else {
      // Al desactivar: limpiar session_ids (la lógica vuelve a ser por evento)
      this.form.get('session_ids')!.setValue([]);
      this.openEventSessionDropdowns.clear();
    }
  }

  // Validación: cuando session_strict_mode está activo y rule_type es volume/bundle/pass,
  // cada evento seleccionado debe tener al menos una sesión marcada.
  hasInvalidSessionSelection(): boolean {
    if (!this.form.get('session_strict_mode')?.value) return false;
    const ruleType = this.form.get('rule_type')?.value;
    if (!['volume', 'bundle', 'pass'].includes(ruleType)) return false;
    if (this.form.get('apply_scope')?.value === 'company') return false;

    const selectedEventIds: number[] = this.form.get('event_ids')?.value || [];
    return selectedEventIds.some(eventId => {
      const sessions = this.getSessionsForEvent(eventId);
      return sessions.length > 0 && this.getSelectedSessionsCount(eventId) === 0;
    });
  }

  selectCategory(catId: number | null) {
    this.eventCategoryFilter = catId;
    this.categoryDropdownOpen = false;
  }

  getSelectedCategoryName(): string {
    if (!this.eventCategoryFilter) return 'Todas las categorías';
    const cat = this.eventCategories.find(c => c.id === this.eventCategoryFilter);
    return cat?.name || 'Categoría';
  }

  getSelectedCategoryIcon(): string {
    if (!this.eventCategoryFilter) return 'fa-grid-2';
    const cat = this.eventCategories.find(c => c.id === this.eventCategoryFilter);
    return cat?.icon || 'fa-tag';
  }

  getCategoryIcon(categoryId: number): string {
    const cat = this.eventCategories.find(c => c.id === categoryId);
    return cat?.icon || 'fa-tag';
  }

  onRuleTypeChange() {
    const ruleType = this.form.get('rule_type')?.value;

    if (ruleType !== 'promo_code') {
      while (this.promoCodes.length) this.promoCodes.removeAt(0);
    }

    const discountMode = this.form.get('discount_mode')!;
    const discountValue = this.form.get('discount_value')!;
    const buyQuantity = this.form.get('buy_quantity')!;
    const payQuantity = this.form.get('pay_quantity')!;
    const minQuantity = this.form.get('min_quantity')!;

    if (ruleType === 'bundle') {
      discountMode.clearValidators();
      discountValue.clearValidators();
      buyQuantity.setValidators([Validators.required, Validators.min(2)]);
      payQuantity.setValidators([Validators.required, Validators.min(1)]);
      minQuantity.clearValidators();
    } else if (ruleType === 'volume') {
      discountMode.setValidators([Validators.required]);
      discountValue.setValidators([Validators.required, Validators.min(0)]);
      buyQuantity.clearValidators();
      payQuantity.clearValidators();
      minQuantity.setValidators([Validators.required, Validators.min(1)]);
    } else {
      discountMode.setValidators([Validators.required]);
      discountValue.setValidators([Validators.required, Validators.min(0)]);
      buyQuantity.clearValidators();
      payQuantity.clearValidators();
      minQuantity.clearValidators();
    }

    discountMode.updateValueAndValidity();
    discountValue.updateValueAndValidity();
    buyQuantity.updateValueAndValidity();
    payQuantity.updateValueAndValidity();
    minQuantity.updateValueAndValidity();
  }

  save() {
    if (this.form.invalid || this.saving) return;
    if (this.hasInvalidSessionSelection()) {
      this.openInfoCard('Cada evento debe tener al menos una sesión seleccionada');
      return;
    }
    this.saving = true;

    const formValue = { ...this.form.value };

    // Set company
    formValue.id_company = this.business?.id;

    // Public name
    if (!formValue.public_name) delete formValue.public_name;

    // Clean up null values
    ['starts_at', 'ends_at', 'min_quantity', 'min_cart_amount', 'buy_quantity', 'pay_quantity', 'application_target'].forEach(key => {
      if (!formValue[key]) formValue[key] = null;
    });

    // allowed_channels: ensure array (empty = all channels)
    if (!formValue.allowed_channels) {
      formValue.allowed_channels = [];
    }

    // bundle: strip discount_mode and discount_value, validate pay_quantity < buy_quantity
    if (formValue.rule_type === 'bundle') {
      delete formValue.discount_mode;
      delete formValue.discount_value;
      delete formValue.max_discount_amount;
      if (formValue.pay_quantity >= formValue.buy_quantity) {
        this.saving = false;
        this.openInfoCard('La cantidad "Paga" debe ser menor que la cantidad "Compra"');
        return;
      }
    } else {
      // max_discount_amount: only relevant for percentage mode
      if (formValue.discount_mode !== 'percentage' || !formValue.max_discount_amount) {
        formValue.max_discount_amount = null;
      }
    }

    // Handle promo code (simplified single code)
    if (formValue.rule_type === 'promo_code' && formValue.promo_code_value) {
      formValue.code = formValue.promo_code_value.toUpperCase();
      formValue.max_uses_global = formValue.max_uses_global || null;
      formValue.max_uses_per_customer = formValue.max_uses_per_customer || null;
    } else {
      delete formValue.code;
      delete formValue.max_uses_global;
      delete formValue.max_uses_per_customer;
    }
    delete formValue.promo_code_value;
    delete formValue.promo_codes;

    // Remove bulk field if not applicable
    if (!this.isSuperAdmin || !formValue.all_companies) {
      delete formValue.all_companies;
    }

    if (this.isEdit && this.discountId) {
      this.discountService.updateDiscount(this.discountId, formValue).subscribe({
        next: () => {
          this.saving = false;
          this.openInfoCard('Descuento actualizado correctamente');
          this.router.navigate(['/panel-control/discounts']);
        },
        error: (err) => {
          this.saving = false;
          const msg = err.error?.message || 'Error al actualizar el descuento';
          this.openInfoCard(msg);
        }
      });
    } else {
      this.discountService.createDiscount(formValue).subscribe({
        next: () => {
          this.saving = false;
          this.openInfoCard('Descuento creado correctamente');
          this.router.navigate(['/panel-control/discounts']);
        },
        error: (err) => {
          this.saving = false;
          const msg = err.error?.message || 'Error al crear el descuento';
          this.openInfoCard(msg);
        }
      });
    }
  }

  onStartsAtChange(date: Date) {
    if (date) {
      this.startsAtDate = date;
      this.form.patchValue({ starts_at: formatDate(date, 'yyyy-MM-ddTHH:mm', 'es') });
    } else {
      this.startsAtDate = null;
      this.form.patchValue({ starts_at: null });
    }
  }

  onEndsAtChange(date: Date) {
    if (date) {
      this.endsAtDate = date;
      this.form.patchValue({ ends_at: formatDate(date, 'yyyy-MM-ddTHH:mm', 'es') });
    } else {
      this.endsAtDate = null;
      this.form.patchValue({ ends_at: null });
    }
  }

  cancel() {
    this.router.navigate(['/panel-control/discounts']);
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.businessSub?.unsubscribe();
    this.panelTitleService.setSubtitle(null);
  }
}
